import { PrivateBlogDomainService } from './domain';
import { PrivateBlogCategoryFactory } from './entity/privateBlogCategoryFactory';
import { PrivateBlogFactory } from './entity/privateBlogFactory';

import type { PrivateBlogCategoryEntity } from './entity/privateBlogCategoryEntity';
import type { PrivateBlogCategoryIRepository } from './entity/privateBlogCategoryIRepository';
import type { PrivateBlogEntity } from './entity/privateBlogEntity';
import type { PrivateBlogIRepository } from './entity/privateBlogIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';
import type { PrivateBlogCategoryJson } from '@/types/domain/privateBlog/entities/privateBlogCategoryEntity';
import type { PrivateBlogJson } from '@/types/domain/privateBlog/entities/privateBlogEntity';

/**
 * アプリケーションサービス
 */
export class PrivateBlogApplicationService {
  private service!: PrivateBlogDomainService;

  private privateBlogCategoryFactory = new PrivateBlogCategoryFactory();

  private privateBlogFactory = new PrivateBlogFactory();

  /**
   * Inject
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param privateBlogCategoryRepository privateBlogCategoryRepository
   * @param privateBlogRepository privateBlogRepository
   * @param logger ロガー
   */
  public inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    privateBlogCategoryRepository: PrivateBlogCategoryIRepository,
    privateBlogRepository: PrivateBlogIRepository,
    logger: Logger,
  ) {
    this.service = new PrivateBlogDomainService(
      wagakoNurseryApi,
      privateBlogCategoryRepository,
      privateBlogRepository,
      logger,
    );
  }

  /**
   * カテゴリ一覧を取得する.
   * @param nurseryId nurseryId.
   */
  public async getCategoryList(nurseryId: string) {
    const entityList = await this.service.getCategoryList(nurseryId);
    const items = entityList.map((v: PrivateBlogCategoryEntity) => v.toJsonData());
    return {
      /** データ */
      items,
    };
  }

  /**
   * 限定公開ページ一覧を取得する.
   * @param nurseryId nurseryId.
   * @param token token.
   */
  public async getPrivateBlogList(nurseryId: string, categoryId: string, token: string | null) {
    const result = await this.service.getPrivateBlogList(nurseryId, categoryId, token);
    const { nextToken } = result;
    const items = result.items.map((v: PrivateBlogEntity) => v.toJsonData());
    return {
      /** Nextトークン */
      nextToken,
      /** データ */
      items,
    };
  }

  /**
   * 限定コンテンツをロードする.
   * @param item Json.
   * @param nurseryId string.
   */
  public async loadContentPrivateBlog(
    item: PrivateBlogJson,
    nurseryId: string,
  ) {
    const entity = this.privateBlogFactory.createFromJsonPvt(item);
    return this.service.loadContent(entity.s3Path, nurseryId);
  }

  /**
   * 限定ページリストを更新(Create)
   * @param privateBlogList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createPrivateBlogStore(
    privateBlogList: PrivateBlogJson[],
    payload: EntitySubscribeType,
    filter: ((json: PrivateBlogJson) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePvt(
      privateBlogList,
      payload,
      this.privateBlogFactory,
      filter,
    );
  }

  /**
   * 限定ページリストを更新(Update)
   * @param privateBlogList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updatePrivateBlogStore(
    privateBlogList: PrivateBlogJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePvt(
      privateBlogList,
      payload,
      this.privateBlogFactory,
    );
  }

  /**
   * 限定ページリストを更新(Delete)
   * @param privateBlogList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deletePrivateBlogStore(
    privateBlogList: PrivateBlogJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePvt(
      privateBlogList,
      payload,
      this.privateBlogFactory,
    );
  }

  /**
   * カテゴリリストを更新(Create)
   * @param categoryList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createPrivateBlogCategoryStore(
    categoryList: PrivateBlogCategoryJson[],
    payload: EntitySubscribeType,
    filter: ((json: PrivateBlogCategoryJson) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePvt(
      categoryList,
      payload,
      this.privateBlogCategoryFactory,
      filter,
    );
  }

  /**
   * カテゴリリストを更新(Update)
   * @param categoryList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updatePrivateBlogCategoryStore(
    categoryList: PrivateBlogCategoryJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePvt(
      categoryList,
      payload,
      this.privateBlogCategoryFactory,
    );
  }

  /**
   * カテゴリリストを更新(Delete)
   * @param categoryList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deletePrivateBlogCategoryStore(
    categoryList: PrivateBlogCategoryJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePvt(
      categoryList,
      payload,
      this.privateBlogCategoryFactory,
    );
  }
}
